// ========== Authentication ==========
export enum UserRole {
  USER = 'USER',
  ADMIN = 'ADMIN'
}

export interface User {
  id: number;
  firstName: string;
  lastName: string;
  username: string;
  role: UserRole;
  active: boolean;
  createdAt?: string;
  updatedAt?: string;
}

export interface AuthResponse {
  user: User;
  accessToken: string;
  refreshToken: string;
}

export interface LoginRequest {
  username: string;
  password: string;
}

export interface CreateUserRequest {
  firstName: string;
  lastName: string;
  username: string;
  password: string;
  role: UserRole;
}

export interface UpdateUserRequest {
  firstName: string;
  lastName: string;
  username: string;
  password?: string;
  role: UserRole;
  active: boolean;
}

export interface ChangePasswordRequest {
  oldPassword: string;
  newPassword: string;
}

// ========== Aktivitätsprotokoll (Audit-Log) ==========
export type ActivityAction =
  | 'LOGIN' | 'LOGIN_FAILED' | 'LOGOUT' | 'CREATE' | 'UPDATE' | 'DELETE';

export interface ActivityLogEntry {
  id: number;
  timestamp?: string;
  username?: string;
  userId?: number;
  action: ActivityAction;
  entityType?: string;
  entityId?: string;
  httpMethod?: string;
  path?: string;
  statusCode?: number;
  ipAddress?: string;
  details?: string;
}

export interface ActivityLogPage {
  items: ActivityLogEntry[];
  total: number;
  page: number;
  size: number;
}

export interface ActivityQuery {
  action?: string;
  username?: string;
  from?: string;
  to?: string;
  page?: number;
  size?: number;
}

// ========== Shared ==========
// A polygon: outer ring as a list of [lon, lat] points (GeoJSON convention).
export type Polygon = number[][];

export const FRIEDHOF_STATUS = ['AKTIV', 'INAKTIV', 'VOLL_BELEGT'] as const;
export const GRAB_STATUS = ['FREI', 'BELEGT', 'RESERVIERT', 'IN_NUTZUNG'] as const;
export const ZAHLUNGSINTERVALL = ['MONATLICH', 'QUARTALSWEISE', 'HALBJAEHRLICH', 'JAEHRLICH'] as const;
export const VERTRAG_STATUS = ['AKTIV', 'BEENDET'] as const;

// ========== Friedhof ==========
export interface Friedhof {
  id: number;
  name: string;
  adresse: string;
  anzahlGrabstaetten: number;
  status: string;
  notizen?: string;
  active: boolean;
  createdAt?: string;
  updatedAt?: string;
}

export interface CreateFriedhofRequest {
  name: string;
  adresse: string;
  anzahlGrabstaetten: number;
  status: string;
  notizen?: string;
}

export interface UpdateFriedhofRequest extends CreateFriedhofRequest {
  active: boolean;
}

// ========== Kunde ==========
export const ANREDEN = ['Herr', 'Frau', 'Familie', 'Firma'] as const;

export interface Kunde {
  id: number;
  kundennummer: string;
  anrede?: string;
  vorname: string;
  nachname: string;
  strasse?: string;
  hausnummer?: string;
  plz?: string;
  ort?: string;
  telefon?: string;
  mobil?: string;
  email?: string;
  notizen?: string;
  active: boolean;
  createdAt?: string;
  updatedAt?: string;
}

export interface CreateKundeRequest {
  anrede?: string;
  vorname: string;
  nachname: string;
  strasse?: string;
  hausnummer?: string;
  plz?: string;
  ort?: string;
  telefon?: string;
  mobil?: string;
  email?: string;
  notizen?: string;
}

export interface UpdateKundeRequest extends CreateKundeRequest {
  active: boolean;
}

// ========== Grab ==========
export interface Grab {
  id: number;
  friedhofId: number;
  friedhofName?: string;
  abteilung: string;
  grabnummer: string;
  status: string;
  lageplanPolygon?: Polygon;
  notizen?: string;
  // Single-tier fields kept for backward compatibility (first currently-buried animal).
  tierId?: number;
  tierName?: string;
  tierArt?: string;
  // Currently buried animals (active/ongoing contract or no contract).
  tiere?: TierKurz[];
  // Number of animals that are part of the grave's history (ended contract).
  historieAnzahl?: number;
  kundeId?: number;
  kundeName?: string;
  kundennummer?: string;
  active: boolean;
  createdAt?: string;
  updatedAt?: string;
}

export interface TierKurz {
  id: number;
  name?: string;
  art?: string;
}

export interface CreateGrabRequest {
  friedhofId: number;
  abteilung: string;
  grabnummer: string;
  status?: string;
  lageplanPolygon?: Polygon;
  notizen?: string;
  kundeId?: number;
}

export interface UpdateGrabRequest extends CreateGrabRequest {
  active: boolean;
}

// ========== Vertrag ==========
export interface Vertrag {
  id: number;
  vertragsnummer: string;
  kundeId: number;
  kundeName?: string;
  kundennummer?: string;
  grabId: number;
  grabNummer?: string;
  friedhofName?: string;
  tierId?: number;
  tierName?: string;
  unterschriftsdatum: string;
  laufzeitJahre: number;
  autoVerlaengerung: boolean;
  verlaengerungJahre: number;
  zahlungsintervall: string;
  status?: string;
  endeDatum?: string;
  notizen?: string;
  active: boolean;
  createdAt?: string;
  updatedAt?: string;
  // Ab wann dieser Vertrag abgerechnet wird; fehlt bei Altbestand (dann gilt der globale Stichtag).
  abrechnungAb?: string | null;
  // Nur in der Antwort aufs Anlegen: die erzeugten Rechnungsentwürfe bzw. warum keiner entstand.
  erstrechnungId?: number;
  erstrechnungIds?: number[];
  erstrechnungHinweis?: string;
}

interface VertragBasis {
  kundeId: number;
  grabId: number;
  tierId?: number;
  unterschriftsdatum: string;
  laufzeitJahre: number;
  notizen?: string;
}

// Beim Anlegen steuern Verlängerung/Zahlungsintervall die Abrechnung nicht mehr (Backend-Defaults);
// abgerechnet wird, was in `positionen` beauftragt ist.
export interface CreateVertragRequest extends VertragBasis {
  autoVerlaengerung?: boolean;
  verlaengerungJahre?: number;
  zahlungsintervall?: string;
  positionen?: CreateVertragsartikelRequest[];
  // true = gleich die Entwürfe für alles erzeugen, was ab `abrechnungAb` bis heute fällig ist.
  erstrechnung?: boolean;
  // Ab wann abgerechnet wird (ISO); ohne Angabe das Unterschriftsdatum. „Erst ab jetzt“ = heute.
  abrechnungAb?: string;
}

export interface UpdateVertragRequest extends VertragBasis {
  autoVerlaengerung: boolean;
  verlaengerungJahre: number;
  zahlungsintervall: string;
  active: boolean;
}

export interface BeendeVertragRequest {
  endeDatum: string;
}

// ========== Tier ==========
export interface Tier {
  id: number;
  grabId: number;
  grabNummer?: string;
  friedhofName?: string;
  name?: string;
  art?: string;
  rasse?: string;
  gebDatum?: string;
  sterbeDatum?: string;
  gewicht?: number;
  groesse?: string;
  beisetzungsdatum?: string;
  // Derived from the animal's own (optional) contract; owner falls back to the grave's direct customer.
  vertragId?: number;
  vertragsnummer?: string;
  vertragStatus?: string;
  kundeId?: number;
  kundeName?: string;
  active: boolean;
  createdAt?: string;
  updatedAt?: string;
}

export interface CreateTierRequest {
  grabId: number;
  name?: string;
  art?: string;
  rasse?: string;
  gebDatum?: string;
  sterbeDatum?: string;
  gewicht?: number;
  groesse?: string;
  beisetzungsdatum?: string;
}

export interface UpdateTierRequest extends CreateTierRequest {
  active: boolean;
}

// ========== Kommentar ==========
export type KommentarEntityTyp = 'GRAB' | 'KUNDE';

export interface Kommentar {
  id: number;
  entityType: KommentarEntityTyp;
  entityId: number;
  userId: number;
  autorName: string;
  inhalt: string;
  bearbeitet: boolean;
  createdAt?: string;
  updatedAt?: string;
}

export interface CreateKommentarRequest {
  inhalt: string;
}

export interface UpdateKommentarRequest {
  inhalt: string;
}

// ========== Dokument (Datei-Anhänge) ==========
export type DokumentEntityTyp = 'GRAB' | 'KUNDE' | 'VERTRAG';
export type DokumentTyp = 'VERTRAG' | 'URKUNDE' | 'RECHNUNG' | 'FOTO' | 'SONSTIGES';

export const DOKUMENT_TYP: { value: DokumentTyp; label: string }[] = [
  { value: 'VERTRAG', label: 'Vertrag' },
  { value: 'URKUNDE', label: 'Urkunde' },
  { value: 'RECHNUNG', label: 'Rechnung' },
  { value: 'FOTO', label: 'Foto' },
  { value: 'SONSTIGES', label: 'Sonstiges' },
];

export interface Dokument {
  id: number;
  entityType: DokumentEntityTyp;
  entityId: number;
  dokumentTyp: DokumentTyp;
  dateiname: string;
  contentType: string;
  groesseBytes: number;
  userId: number;
  autorName: string;
  createdAt?: string;
  updatedAt?: string;
}

// ========== Lageplan ==========
export interface LageplanGrab {
  grabId: number;
  abteilung: string;
  grabnummer: string;
  status: string;
  // Outer ring as [lon, lat] points (GeoJSON convention).
  polygon?: Polygon;
  tierName?: string;
  tierArt?: string;
  // Number of animals currently buried in the grave (0, 1 or more).
  tierAnzahl?: number;
  // True if a currently buried animal weighs at least 25 kg (heavy-animal marker on the map).
  schwergewicht?: boolean;
  kundeId?: number;
  kundeName?: string;
  vertragsnummer?: string;
}

export interface LageplanAnsicht {
  friedhofId: number;
  name: string;
  // Admin-configured colors per main section, e.g. { "1": "#2563eb" }.
  abteilungFarben: Record<string, string>;
  // Default base map: LUFTBILD | KARTE | WEISS | SATELLIT.
  lageplanHintergrund: string;
  graeber: LageplanGrab[];
}

export interface GeoJsonImportErgebnis {
  angelegt: number;
  aktualisiert: number;
  uebersprungen: number;
  graeber: Grab[];
}

// One transcribed ledger row: a grave plus, optionally, the animal buried in it.
export interface StammdatenImportZeile {
  abteilung: string;
  grabnummer: string;
  status?: string;
  notizen?: string;
  art?: string;
  name?: string;
  rasse?: string;
  gebDatum?: string;
  sterbeDatum?: string;
  beisetzungsdatum?: string;
}

export interface StammdatenImportRequest {
  zeilen: StammdatenImportZeile[];
}

export interface StammdatenImportErgebnis {
  graeberAngelegt: number;
  graeberAktualisiert: number;
  tiereAngelegt: number;
  tiereUebersprungen: number;
  warnungen: string[];
}

// ========== Artikel (Preisliste) ==========
export interface Artikel {
  id: number;
  artikelnummer: string;
  bezeichnung: string;
  // Netto-Preis in Euro; fehlt bei Sammel-/Kopfartikeln der Preisliste.
  preis?: number | null;
  // MwSt in Prozent (Default 19). Kommt NICHT aus dem Excel-Import und überlebt ihn daher.
  mwstSatz: number;
  // Leistungszeitpunkt (Periode), z. B. „Frühjahr" + „15.03." – wie mwstSatz nur im Portal gepflegt.
  periodeId?: number | null;
  periodeName?: string | null;
  periodeTerminText?: string | null;
  active: boolean;
  createdAt?: string;
  updatedAt?: string;
}

export interface CreateArtikelRequest {
  artikelnummer: string;
  bezeichnung: string;
  preis?: number | null;
  mwstSatz?: number | null;
  periodeId?: number | null;
}

// Die Artikelnummer bleibt beim Bearbeiten unverändert – sie ist der Schlüssel des Excel-Imports.
export interface UpdateArtikelRequest {
  bezeichnung: string;
  preis?: number | null;
  mwstSatz?: number | null;
  periodeId?: number | null;
  active: boolean;
}

export interface ArtikelImportErgebnis {
  angelegt: number;
  aktualisiert: number;
  unveraendert: number;
  uebersprungen: number;
  warnungen: string[];
}

// ========== Periode (Abrechnungs-Zeitpunkte) ==========
export interface Periode {
  id: number;
  name: string;
  // Jährlich wiederkehrender Leistungszeitpunkt als Tag+Monat; beide fehlen = kein Termin.
  // Kein Abrechnungszeitpunkt — abgerechnet wird gebündelt einmal im Jahr.
  tag?: number | null;
  monat?: number | null;
  // Fertig formatiert, z. B. "15.03."; null ohne Termin.
  terminText?: string | null;
  notizen?: string | null;
  anzahlPositionen: number;
  active: boolean;
  createdAt?: string;
  updatedAt?: string;
}

export interface CreatePeriodeRequest {
  name: string;
  tag?: number | null;
  monat?: number | null;
  notizen?: string | null;
}

export interface UpdatePeriodeRequest {
  name: string;
  tag?: number | null;
  monat?: number | null;
  notizen?: string | null;
  active: boolean;
}

// ========== Vertragsartikel (Positionen eines Vertrags) ==========
// Wie oft abgerechnet wird: PACHT folgt dem Vertragsrhythmus (Erstlaufzeit, dann Verlängerungen),
// JAEHRLICH jedes Jahr zum Leistungszeitpunkt der Periode, EINMALIG genau ein Mal.
export const ABRECHNUNGSART = ['PACHT', 'JAEHRLICH', 'EINMALIG', 'VERLAENGERUNG'] as const;
export type Abrechnungsart = typeof ABRECHNUNGSART[number];

// Kurzform für Badges/Listen.
export const ABRECHNUNGSART_LABEL: Record<Abrechnungsart, string> = {
  PACHT: 'Grabpacht',
  JAEHRLICH: 'Jährlich',
  EINMALIG: 'Einmalig',
  VERLAENGERUNG: 'Pachtverlängerung',
};

export interface Vertragsartikel {
  id: number;
  vertragId: number;
  artikelId: number;
  artikelnummer?: string | null;
  artikelBezeichnung?: string | null;
  // Der Artikel wurde inzwischen aus der Preisliste gelöscht – die Position bleibt gültig.
  artikelGeloescht: boolean;
  abrechnungsart: Abrechnungsart;
  // Nur bei JAEHRLICH gesetzt.
  periodeId?: number | null;
  periodeName?: string | null;
  periodeTerminText?: string | null;
  gueltigAbJahr: number;
  // null = unbefristet; gleich gueltigAbJahr = nur dieses eine Jahr.
  gueltigBisJahr?: number | null;
  menge: number;
  einzelpreis: number;
  mwstSatz: number;
  // Berechnet im Backend (BigDecimal, HALF_UP) – hier nur anzeigen, nicht nachrechnen.
  nettoBetrag: number;
  mwstBetrag: number;
  bruttoBetrag: number;
  notizen?: string | null;
  active: boolean;
  createdAt?: string;
  updatedAt?: string;
}

export interface MwstAnteil {
  satz: number;
  netto: number;
  mwst: number;
}

export interface VertragsartikelUebersicht {
  vertragId: number;
  positionen: Vertragsartikel[];
  summeNetto: number;
  mwstJeSatz: MwstAnteil[];
  summeMwst: number;
  summeBrutto: number;
  // Hinweis fürs Umstellen des Altbestands: der Vertrag hat noch keine Pachtposition.
  hatPachtposition: boolean;
}

// Preis, MwSt und Startjahr sind optional: ohne Angabe gilt der aktuelle Stand des Artikels
// bzw. das Vertragsjahr.
export interface CreateVertragsartikelRequest {
  artikelId: number;
  abrechnungsart: Abrechnungsart;
  periodeId?: number | null;
  gueltigAbJahr?: number | null;
  gueltigBisJahr?: number | null;
  menge?: number | null;
  einzelpreis?: number | null;
  mwstSatz?: number | null;
  notizen?: string | null;
}

export interface UpdateVertragsartikelRequest {
  artikelId: number;
  abrechnungsart: Abrechnungsart;
  periodeId?: number | null;
  gueltigAbJahr: number;
  gueltigBisJahr?: number | null;
  menge: number;
  einzelpreis: number;
  mwstSatz: number;
  notizen?: string | null;
  active: boolean;
}

// ========== Rechnung ==========
// ENTWURF: frei änderbar, noch ohne Nummer. GESTELLT: Nummer vergeben, PDF eingefroren,
// unveränderlich. BEZAHLT: Zahlungseingang. STORNIERT: durch eine Storno-Rechnung aufgehoben.
export const RECHNUNG_STATUS = ['ENTWURF', 'GESTELLT', 'BEZAHLT', 'STORNIERT'] as const;
export type RechnungStatus = typeof RECHNUNG_STATUS[number];

export interface Rechnungsposition {
  id: number;
  positionNr: number;
  bezeichnung: string;
  menge: number;
  einzelpreis: number;
  mwstSatz: number;
  netto: number;
  mwst: number;
  brutto: number;
  // § 14 UStG: Zeitpunkt bzw. Zeitraum der Leistung
  leistungVon?: string | null;
  leistungBis?: string | null;
  vertragId?: number | null;
  vertragsnummer?: string | null;
  vertragsartikelId?: number | null;
  abrechnungsjahr?: number | null;
}

export interface Rechnung {
  id: number;
  // Fehlt beim Entwurf – die Nummer wird erst beim Stellen vergeben.
  rechnungsnummer?: string | null;
  kundeId: number;
  kundeName?: string | null;
  kundennummer?: string | null;
  status: RechnungStatus;
  abrechnungsjahr: number;
  rechnungsdatum?: string | null;
  faelligAm?: string | null;
  ueberfaellig: boolean;
  empfaengerName?: string | null;
  empfaengerZusatz?: string | null;
  empfaengerStrasse?: string | null;
  empfaengerPlz?: string | null;
  empfaengerOrt?: string | null;
  empfaengerKundennummer?: string | null;
  positionen: Rechnungsposition[];
  summeNetto: number;
  mwstJeSatz: MwstAnteil[];
  summeMwst: number;
  summeBrutto: number;
  // Storno-Kette in beide Richtungen
  storniertVonRechnungId?: number | null;
  storniertVonRechnungsnummer?: string | null;
  storniertRechnungId?: number | null;
  storniertRechnungsnummer?: string | null;
  hatPdf: boolean;
  notizen?: string | null;
  createdAt?: string;
  updatedAt?: string;
}

export interface UpdateRechnungRequest {
  empfaengerName?: string | null;
  empfaengerZusatz?: string | null;
  empfaengerStrasse?: string | null;
  empfaengerPlz?: string | null;
  empfaengerOrt?: string | null;
  notizen?: string | null;
}

// Schnellrechnung von Hand; `stellen` stellt sie sofort (Nummer + PDF).
export interface CreateRechnungRequest {
  kundeId: number;
  vertragId?: number | null;
  positionen: CreateRechnungspositionRequest[];
  stellen?: boolean;
}

export interface CreateRechnungspositionRequest {
  bezeichnung: string;
  menge?: number | null;
  einzelpreis: number;
  mwstSatz?: number | null;
  leistungVon?: string | null;
  leistungBis?: string | null;
}

// Vorschau: eine Rechnung, die der tägliche Lauf an einem kommenden Jahrestag anlegen wird.
// Rein rechnerisch, nicht gespeichert – wie eine vorgemerkte Buchung auf dem Girokonto.
export interface VorgemerkteRechnung {
  erstelltAm: string;
  vertragId: number;
  vertragsnummer?: string | null;
  kundeId: number;
  kundeName?: string | null;
  abrechnungsjahr: number;
  positionen: { bezeichnung: string; menge: number; brutto: number; leistungVon: string; leistungBis: string }[];
  summeNetto: number;
  summeBrutto: number;
}

export interface Rechnungsvorschau {
  von: string;
  bis: string;
  rechnungen: VorgemerkteRechnung[];
  hinweis?: string | null;
}

export interface RechnungslaufErgebnis {
  // Der Tag, für den die fälligen Jahrestage geprüft wurden (ISO).
  stichtag: string;
  entwuerfeErstellt: number;
  vertraege: number;
  positionen: number;
  hinweise: string[];
}

// Abrechnungs-Historie am Vertrag: welche Position, welches Jahr, welche Rechnung.
export interface AbrechnungHistorieEintrag {
  vertragsartikelId?: number | null;
  abrechnungsjahr?: number | null;
  brutto: number;
  rechnungId: number;
  rechnungsnummer?: string | null;
  status: RechnungStatus;
  rechnungsdatum?: string | null;
}

// ========== Rechnungseinstellungen ==========
export interface Rechnungseinstellungen {
  firmenname?: string | null;
  zusatz?: string | null;
  strasse?: string | null;
  plz?: string | null;
  ort?: string | null;
  ustId?: string | null;
  steuernummer?: string | null;
  iban?: string | null;
  bic?: string | null;
  bank?: string | null;
  zahlungszielTage: number;
  rechnungslaufTag: number;
  rechnungslaufMonat: number;
  abrechnungAb?: string | null;
  rechnungsnummerPraefix: string;
  fusszeile?: string | null;
  telefon?: string | null;
  mobil?: string | null;
  email?: string | null;
  geschaeftsfuehrer?: string | null;
  bank2?: string | null;
  iban2?: string | null;
  bic2?: string | null;
  fussZusatz?: string | null;
  // Solange nicht leer, lässt sich keine Rechnung stellen.
  fehlendePflichtangaben: string[];
}

export type UpdateRechnungseinstellungenRequest = Omit<Rechnungseinstellungen, 'fehlendePflichtangaben'>;
